import { Trophy, TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCategoryTranslation } from '../../hooks/useCategoryTranslation';
import type { CategoryBreakdown } from '../../types';

interface TopCategoriesCardProps {
  categories: CategoryBreakdown[];
  formatCurrency: (amount: number) => string;
  noCard?: boolean;
}

const medals = ['🥇', '🥈', '🥉'];

export default function TopCategoriesCard({ categories, formatCurrency, noCard }: TopCategoriesCardProps) {
  const { t } = useTranslation('dashboard');
  const { translateCategoryName } = useCategoryTranslation();
  if (categories.length === 0) {
    const emptyContent = (
      <>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{t('topCategoriesDetails.title')}</h3>
          <div className="p-2 rounded-lg bg-amber-100">
            <Trophy className="w-5 h-5 text-amber-600" />
          </div>
        </div>
        <div className="flex flex-col items-center justify-center h-32 text-gray-500">
          <TrendingUp className="w-10 h-10 mb-2 opacity-50" />
          <p className="text-sm">{t('topCategoriesDetails.noExpenses')}</p>
        </div>
      </>
    );
    if (noCard) return emptyContent;
    return <div className="card h-full">{emptyContent}</div>;
  }

  const content = (
    <>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{t('topCategoriesDetails.title')}</h3>
        <div className="p-2 rounded-lg bg-amber-100">
          <Trophy className="w-5 h-5 text-amber-600" />
        </div>
      </div>

      <div className="space-y-3">
        {categories.map((category, index) => (
          <div
            key={category.categoryId}
            className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
          >
            <span className="text-2xl">{medals[index] || '•'}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <p className="font-medium text-gray-900 truncate">{translateCategoryName(category.categoryId, category.categoryName, category.categoryIsDefault)}</p>
                <p className="font-semibold text-gray-900 ml-2">{formatCurrency(category.amount)}</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${category.percentage}%`,
                      backgroundColor: category.color,
                    }}
                  />
                </div>
                <span className="text-xs text-gray-500 w-12 text-right">
                  {category.percentage.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );

  if (noCard) return content;
  return <div className="card h-full">{content}</div>;
}
